import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const CV_URL = '/cv-dayron-hernandez.pdf';
const FORM_NAME = 'cv-download';

const encode = data =>
  Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
`;

const StyledModal = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  padding: 40px 34px 34px;
  background-color: var(--light-navy);
  border: 1px solid var(--lightest-navy);
  border-radius: var(--border-radius);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);

  @media (max-width: 480px) {
    padding: 34px 22px 26px;
  }

  .close {
    position: absolute;
    top: 14px;
    right: 14px;
    display: flex;
    padding: 6px;
    color: var(--light-slate);
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--green);
      outline: none;
    }
    svg {
      width: 20px;
      height: 20px;
    }
  }

  h3 {
    margin: 0 0 6px;
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);
  }
  .sub {
    margin: 0 0 24px;
    color: var(--slate);
    font-size: var(--fz-md);
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--light-slate);
  }

  input {
    width: 100%;
    padding: 12px 14px;
    color: var(--lightest-slate);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    background-color: var(--navy);
    border: 1px solid var(--lightest-navy);
    border-radius: var(--border-radius);
    transition: var(--transition);

    &:focus,
    &:focus-visible {
      border-color: var(--green);
      outline: none;
    }
    &::placeholder {
      color: var(--dark-slate);
    }
  }

  .hp {
    position: absolute;
    left: -9999px;
  }

  .submit {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 8px;
    width: 100%;
    text-align: center;
    padding: 14px 20px;

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .status {
    margin: 12px 0 0;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    text-align: center;
  }
  .status.error {
    color: #ff6b6b;
  }
  .status.done {
    color: var(--green);
  }

  .privacy {
    margin: 16px 0 0;
    color: var(--dark-slate);
    font-size: var(--fz-xxs);
    text-align: center;
  }
`;

const CvDownload = ({ className, children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', correo: '', telefono: '', 'bot-field': '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const firstInput = useRef(null);

  const close = () => {
    setOpen(false);
    setStatus('idle');
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = e => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    if (firstInput.current) {
      firstInput.current.focus();
    }
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const triggerDownload = () => {
    const a = document.createElement('a');
    a.href = CV_URL;
    a.download = 'CV-Dayron-Hernandez.pdf';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': FORM_NAME, ...form }),
      });
      if (!res.ok) {
        throw new Error('Network error');
      }
      setStatus('done');
      triggerDownload();
      setTimeout(close, 1800);
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {children || 'CV'}
      </button>

      {/* Formulario oculto para que Netlify lo detecte en el build */}
      <form name={FORM_NAME} data-netlify="true" netlify-honeypot="bot-field" hidden>
        <input type="text" name="nombre" />
        <input type="email" name="correo" />
        <input type="tel" name="telefono" />
        <input name="bot-field" />
      </form>

      {open &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <StyledOverlay
            onMouseDown={e => {
              if (e.target === e.currentTarget) {
                close();
              }
            }}>
          <StyledModal role="dialog" aria-modal="true" aria-label="Descargar CV">
            <button type="button" className="close" onClick={close} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h3>Descarga mi CV</h3>
            <p className="sub">Déjame tus datos y te llevas mi CV al instante.</p>

            <form name={FORM_NAME} method="post" onSubmit={handleSubmit}>
              <input type="hidden" name="form-name" value={FORM_NAME} />
              <p className="hp">
                <label>
                  No rellenar: <input name="bot-field" onChange={handleChange} />
                </label>
              </p>

              <label>
                Nombre
                <input
                  ref={firstInput}
                  type="text"
                  name="nombre"
                  required
                  placeholder="Tu nombre"
                  value={form.nombre}
                  onChange={handleChange}
                />
              </label>
              <label>
                Correo
                <input
                  type="email"
                  name="correo"
                  required
                  placeholder="tucorreo@email.com"
                  value={form.correo}
                  onChange={handleChange}
                />
              </label>
              <label>
                Teléfono
                <input
                  type="tel"
                  name="telefono"
                  required
                  placeholder="+34 600 000 000"
                  value={form.telefono}
                  onChange={handleChange}
                />
              </label>

              <button type="submit" className="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Enviando…' : 'Descargar CV'}
              </button>

              {status === 'error' && (
                <p className="status error">Hubo un problema. Inténtalo de nuevo.</p>
              )}
              {status === 'done' && <p className="status done">¡Listo! Descargando tu CV…</p>}
            </form>

            <p className="privacy">Usaré tus datos solo para contactarte. Nada de spam.</p>
          </StyledModal>
          </StyledOverlay>,
          document.body,
        )}
    </>
  );
};

CvDownload.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export default CvDownload;
